import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  ClipboardCheck, CheckCircle2, Clock, ArrowLeft, ArrowRight, Sparkles,
  Trophy, AlertCircle, Loader2,
} from "lucide-react";
import { useT } from "@/lib/i18n";

function ScalePicker({ value, onChange, idx }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          onClick={() => onChange(n)}
          data-testid={`scale-${idx}-${n}`}
          className={`h-10 w-10 rounded-full border-2 text-sm font-medium transition-all ${
            value === n
              ? "bg-[#1B2E7A] border-[#1B2E7A] text-white"
              : "bg-white border-[#E5E7EB] text-[#1A1A1A] hover:border-[#1B2E7A]"
          }`}
          aria-label={`Escala ${n}`}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

function MultipleChoice({ options, value, onChange, idx }) {
  return (
    <div className="space-y-2">
      {options.map((opt, i) => (
        <button
          key={i}
          onClick={() => onChange(i)}
          data-testid={`mc-${idx}-${i}`}
          className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${
            value === i
              ? "bg-[#1B2E7A]/5 border-[#1B2E7A] text-[#1A1A1A]"
              : "bg-white border-[#E5E7EB] text-[#1A1A1A] hover:border-[#1B2E7A]/40"
          }`}
        >
          <span className="text-xs uppercase tracking-[0.18em] text-[#6B7280] mr-2">{String.fromCharCode(65 + i)}</span>
          {opt}
        </button>
      ))}
    </div>
  );
}

function EvalRunner({ evaluation, onClose, onSubmitted, t }) {
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [startedAt] = useState(() => Date.now());

  const setAns = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));

  const submit = async () => {
    const unanswered = evaluation.questions.filter((q) => answers[q.id] === undefined || answers[q.id] === "");
    if (unanswered.length > 0) {
      if (!window.confirm(t("eval.confirm_unanswered", { n: unanswered.length }))) return;
    }
    setSubmitting(true);
    try {
      const payload = {
        answers: Object.entries(answers).map(([qid, ans]) => ({
          question_id: qid,
          answer: ans,
        })),
        duration_seconds: Math.round((Date.now() - startedAt) / 1000),
      };
      const r = await api.post(`/evaluations/${evaluation.id}/submit`, payload);
      onSubmitted(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="eval-runner" className="space-y-6">
      <button
        onClick={onClose}
        className="text-xs text-[#6B7280] inline-flex items-center gap-1 hover:text-[#1B2E7A]"
        data-testid="eval-close"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> {t("common.back")}
      </button>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">{t("eval.runner.eyebrow")}</p>
        <h1 className="font-heading text-3xl sm:text-4xl text-[#1A1A1A]">{evaluation.title}</h1>
        {evaluation.description && (
          <p className="text-[#6B7280] mt-2 max-w-2xl">{evaluation.description}</p>
        )}
        <div className="flex items-center gap-3 mt-3 text-xs text-[#6B7280]">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" /> {evaluation.duration_minutes} min
          </span>
          <span>·</span>
          <span>{evaluation.questions.length} {t("eval.questions")}</span>
        </div>
      </div>

      <div className="space-y-5">
        {evaluation.questions.map((q, i) => (
          <div
            key={q.id}
            className="bg-white border border-[#E5E7EB] rounded-2xl p-5"
            data-testid={`eval-q-${i}`}
          >
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280] mb-2">
              {t("eval.question")} {i + 1} · {t(`eval.type.${q.type}`)}
            </p>
            <p className="font-heading text-lg text-[#1A1A1A] mb-4 leading-snug">{q.text}</p>
            {q.type === "multiple_choice" && (
              <MultipleChoice
                idx={i}
                options={q.options || []}
                value={answers[q.id] !== undefined ? Number(answers[q.id]) : null}
                onChange={(v) => setAns(q.id, v)}
              />
            )}
            {q.type === "scale" && (
              <ScalePicker
                idx={i}
                value={answers[q.id] !== undefined ? Number(answers[q.id]) : null}
                onChange={(v) => setAns(q.id, v)}
              />
            )}
            {q.type === "open" && (
              <Textarea
                placeholder={t("eval.open_placeholder")}
                rows={4}
                value={answers[q.id] || ""}
                onChange={(e) => setAns(q.id, e.target.value)}
                data-testid={`open-${i}`}
              />
            )}
          </div>
        ))}
      </div>

      <div className="sticky bottom-4 bg-white border border-[#E5E7EB] rounded-2xl p-4 flex items-center justify-between shadow-lg">
        <div>
          <p className="text-xs text-[#6B7280]">{t("eval.runner.answered")}</p>
          <p className="font-heading text-lg text-[#1A1A1A]">
            {Object.keys(answers).filter((k) => answers[k] !== "" && answers[k] !== null && answers[k] !== undefined).length}/{evaluation.questions.length}
          </p>
        </div>
        <Button
          onClick={submit}
          disabled={submitting}
          className="bg-[#1B2E7A] hover:bg-[#13205A] text-white"
          data-testid="eval-submit"
        >
          {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-1" />}
          {submitting ? t("common.loading") : t("eval.submit")}
        </Button>
      </div>
    </div>
  );
}

function EvalResult({ result, evaluation, onClose, t }) {
  const passed = result.passed;
  const summary = result.summary || {};
  return (
    <div data-testid="eval-result" className="space-y-6">
      <button
        onClick={onClose}
        className="text-xs text-[#6B7280] inline-flex items-center gap-1 hover:text-[#1B2E7A]"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> {t("eval.back_to_list")}
      </button>

      <div
        className={`rounded-2xl p-6 border-2 ${
          passed === true
            ? "bg-[#1B2E7A]/5 border-[#1B2E7A]/30"
            : passed === false
            ? "bg-[#DC2626]/5 border-[#DC2626]/30"
            : "bg-[#F3F4F6] border-[#E5E7EB]"
        }`}
      >
        <div className="flex items-center gap-3 mb-2">
          {passed === true ? (
            <Trophy className="h-7 w-7 text-[#1B2E7A]" />
          ) : passed === false ? (
            <AlertCircle className="h-7 w-7 text-[#DC2626]" />
          ) : (
            <ClipboardCheck className="h-7 w-7 text-[#6B7280]" />
          )}
          <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">{evaluation.title}</p>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A1A]">
          {result.score}<span className="text-2xl text-[#6B7280]">/100</span>
        </h1>
        <p className="text-[#6B7280] mt-2">
          {passed === true && t("eval.result.passed")}
          {passed === false && t("eval.result.failed")}
          {passed === null && t("eval.result.qualitative")}
        </p>
        {result.global_points_awarded > 0 && (
          <p className="text-xs text-[#1B2E7A] mt-2 inline-flex items-center gap-1">
            <Sparkles className="h-3.5 w-3.5" /> +{result.global_points_awarded} {t("eval.points_awarded")}
          </p>
        )}
      </div>

      {(summary.summary_es || summary.strengths_es?.length || summary.to_improve_es?.length) && (
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5" data-testid="eval-ai-summary">
          <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-2 inline-flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5" /> {t("eval.ai_summary")}
          </p>
          {summary.summary_es && (
            <p className="text-[#1A1A1A] mb-4 leading-relaxed">{summary.summary_es}</p>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            {summary.strengths_es?.length > 0 && (
              <div className="bg-[#1B2E7A]/5 rounded-xl p-3">
                <p className="text-xs uppercase tracking-[0.18em] text-[#1B2E7A] mb-2">{t("eval.strengths")}</p>
                <ul className="space-y-1.5">
                  {summary.strengths_es.map((s, i) => (
                    <li key={i} className="text-sm text-[#1A1A1A] inline-flex gap-2 items-start">
                      <CheckCircle2 className="h-3.5 w-3.5 text-[#1B2E7A] mt-1 shrink-0" /> <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {summary.to_improve_es?.length > 0 && (
              <div className="bg-[#DC2626]/5 rounded-xl p-3">
                <p className="text-xs uppercase tracking-[0.18em] text-[#DC2626] mb-2">{t("eval.to_improve")}</p>
                <ul className="space-y-1.5">
                  {summary.to_improve_es.map((s, i) => (
                    <li key={i} className="text-sm text-[#1A1A1A] inline-flex gap-2 items-start">
                      <AlertCircle className="h-3.5 w-3.5 text-[#DC2626] mt-1 shrink-0" /> <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {result.details?.length > 0 && (
        <details className="bg-white border border-[#E5E7EB] rounded-2xl p-5">
          <summary className="text-sm font-medium text-[#1B2E7A] cursor-pointer">
            {t("eval.review_details")}
          </summary>
          <div className="mt-4 space-y-3">
            {result.details.map((d, i) => (
              <div key={d.question_id} className="border-b border-[#E5E7EB] pb-3 last:border-0">
                <p className="text-xs text-[#6B7280] mb-1">{t("eval.question")} {i + 1}</p>
                <p className="text-sm text-[#1A1A1A] mb-1">{d.text}</p>
                {d.type === "multiple_choice" ? (
                  d.correct === true ? (
                    <span className="text-xs text-[#1B2E7A] inline-flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> {t("eval.correct")}
                    </span>
                  ) : (
                    <span className="text-xs text-[#DC2626]">
                      {t("eval.incorrect")}
                    </span>
                  )
                ) : (
                  <span className="text-xs text-[#6B7280]">{t("eval.answer_recorded")}</span>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      <Button onClick={onClose} variant="outline" data-testid="eval-back-list">
        {t("eval.back_to_list")}
      </Button>
    </div>
  );
}

export default function UserEvaluations() {
  const t = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeEval, setActiveEval] = useState(null); // { ...evaluation }
  const [activeResult, setActiveResult] = useState(null); // submission response
  const [starting, setStarting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/evaluations/me");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const start = async (evalId) => {
    setStarting(evalId);
    try {
      const r = await api.get(`/evaluations/${evalId}`);
      setActiveEval(r.data);
      setActiveResult(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally {
      setStarting(null);
    }
  };

  const onSubmitted = (result) => {
    setActiveResult(result);
    load(); // refresh list to update completed status
  };

  const reset = () => {
    setActiveEval(null);
    setActiveResult(null);
  };

  const pending = useMemo(() => items.filter((i) => !i.completed), [items]);
  const completed = useMemo(() => items.filter((i) => i.completed), [items]);

  if (activeResult && activeEval) {
    return <EvalResult result={activeResult} evaluation={activeEval} onClose={reset} t={t} />;
  }
  if (activeEval) {
    return <EvalRunner evaluation={activeEval} onClose={reset} onSubmitted={onSubmitted} t={t} />;
  }

  return (
    <div data-testid="user-evaluations-page" className="space-y-8">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] inline-flex items-center gap-1.5">
          <ClipboardCheck className="h-3.5 w-3.5" /> {t("eval.user.eyebrow")}
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A1A] mb-3">
          {t("eval.user.title")}
        </h1>
        <p className="text-[#6B7280] max-w-2xl">
          {t("eval.user.subtitle")}
        </p>
      </div>

      {loading ? (
        <div className="text-[#374151]">{t("common.loading")}</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-10 text-center">
          <ClipboardCheck className="h-10 w-10 text-[#6B7280] mx-auto mb-2" />
          <p className="text-[#6B7280]">{t("eval.user.empty")}</p>
        </div>
      ) : (
        <>
          <section>
            <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-3">
              {t("eval.user.pending")} · {pending.length}
            </p>
            {pending.length === 0 ? (
              <p className="text-sm text-[#6B7280]">{t("eval.user.no_pending")}</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {pending.map((it) => (
                  <button
                    key={it.evaluation.id}
                    onClick={() => start(it.evaluation.id)}
                    disabled={starting === it.evaluation.id}
                    className="text-left bg-white border-2 border-[#E5E7EB] hover:border-[#1B2E7A] rounded-2xl p-5 transition-all disabled:opacity-50"
                    data-testid={`pending-eval-${it.evaluation.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-xl text-[#1A1A1A] truncate">{it.evaluation.title}</h3>
                        {it.evaluation.description && (
                          <p className="text-xs text-[#6B7280] mt-1 line-clamp-2">{it.evaluation.description}</p>
                        )}
                      </div>
                      <span className="text-[10px] uppercase tracking-[0.18em] text-[#B45309] bg-[#FEF3C7] px-2 py-1 rounded">
                        {t("eval.user.pending_badge")}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[#6B7280] mt-3">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {it.evaluation.duration_minutes} min
                      </span>
                      <span>·</span>
                      <span>{it.evaluation.questions_count} {t("eval.questions")}</span>
                    </div>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#1B2E7A] font-medium">
                      {starting === it.evaluation.id ? (
                        <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("common.loading")}</>
                      ) : (
                        <>{t("eval.user.start")} <ArrowRight className="h-3.5 w-3.5" /></>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>

          {completed.length > 0 && (
            <section>
              <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-3">
                {t("eval.user.completed")} · {completed.length}
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {completed.map((it) => (
                  <div
                    key={it.evaluation.id}
                    className="bg-white border border-[#E5E7EB] rounded-2xl p-5"
                    data-testid={`completed-eval-${it.evaluation.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-lg text-[#1A1A1A] truncate">{it.evaluation.title}</h3>
                      </div>
                      <div className="text-right">
                        <p className={`text-2xl font-heading ${it.passed ? "text-[#1B2E7A]" : "text-[#DC2626]"}`}>
                          {it.score}
                        </p>
                        <p className="text-[10px] text-[#6B7280]">/100</p>
                      </div>
                    </div>
                    <button
                      onClick={() => start(it.evaluation.id)}
                      className="text-xs text-[#1B2E7A] hover:underline"
                    >
                      {t("eval.user.retake")}
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
